import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader, useExport } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  MultiSelect,
  saveBlob,
  Select,
  toast,
} from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Download, Upload } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { invoiceOrderTypeLabel } from "../api/invoice";
import {
  DERINGER_CLIENT_IDS,
  DERINGER_PERIODS,
  deringerPeriodDates,
  openQty,
  OrderClient,
  orderLineTotal,
  SigmClientClient,
  type DeringerPeriod,
  type OrderDTO,
} from "../api/order";
import { NexpimView } from "../views";
import { isoDate } from "../api/reporting";

/**
 * Sales → Orders: booked orders with per-line dispatch detail, and the
 * Deringer 3PL CSV exports — by explicit selection, or by partner clients over
 * a preset window.
 */
export function OrdersPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const { start: startExport, busy } = useExport();
  const client = useMemo(() => new OrderClient(http), [http]);
  const parcelReportInput = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  /**
   * The return leg of the Deringer exchange: their shipment report goes up, and
   * a tracking CSV comes back for the SIGM macro. A direct download rather than
   * a job — the parse is immediate and the operator is standing right there.
   */
  const importParcelReport = async (file: File) => {
    setImporting(true);
    try {
      const { blob, filename } = await client.trackingFromParcelReport(file);
      saveBlob(blob, filename);
      toast.success(
        t("orders.tracking.ready", "Tracking CSV downloaded. Open it with the SIGM import macro."),
      );
    } catch {
      toast.error(t("orders.tracking.failed", "That file could not be read as a shipment report"));
    } finally {
      setImporting(false);
    }
  };
  const allowed = hasView(NexpimView.SALES_ORDERS);

  const [from, setFrom] = useState(() => `${new Date().getFullYear() - 1}-01-01`);
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [selected, setSelected] = useState<OrderDTO[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [periodExportOpen, setPeriodExportOpen] = useState(false);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;

  const { data, isLoading, error } = useQuery({
    queryKey: ["order", "period", from, to],
    queryFn: () => client.byPeriod({ from, to }),
    enabled: allowed && valid,
    staleTime: 60_000,
  });

  const cols = useMemo(() => {
    const c = defineColumns<OrderDTO>();
    return [
      c.text("no", (row) => row.no ?? "", { headerKey: "order.no", priority: 1 }),
      c.text("date", (row) => row.date ?? "", {
        headerKey: "invoice.date",
        filter: "daterange",
        priority: 2,
      }),
      c.custom(
        "type",
        (row) => invoiceOrderTypeLabel(row.order_type),
        (row) => {
          const label = invoiceOrderTypeLabel(row.order_type);
          return (
            <Badge
              variant={label === "ORDER" ? "success" : label === "REFUND" ? "error" : "neutral"}
              size="sm"
            >
              {label}
            </Badge>
          );
        },
        { headerKey: "invoice.type", filter: "select", priority: 3 },
      ),
      c.custom(
        "status",
        (row) => row.status ?? "",
        (row) => (
          <Badge
            variant={row.cancel_date ? "error" : row.status === "OPEN" ? "accent" : "neutral"}
            size="sm"
          >
            {row.cancel_date ? t("order.cancelled", "CANCELLED") : (row.status ?? "")}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.text("currency", (row) => row.currency ?? "", {
        headerKey: "column.currency",
        filter: "select",
        priority: 3,
      }),
      c.text("client", (row) => row.client?.cli_name1 ?? "", {
        headerKey: "column.client",
        priority: 2,
      }),
      c.text("client_order_no", (row) => row.client_order_no ?? "", {
        headerKey: "invoice.client-order-no",
        defaultVisible: false,
      }),
      c.text("warehouse", (row) => row.warehouse ?? "", {
        headerKey: "invoice.warehouse",
        filter: "select",
        defaultVisible: false,
      }),
      c.text("carrier", (row) => row.carrier ?? "", {
        headerKey: "invoice.carrier",
        defaultVisible: false,
      }),
      c.number("lines", (row) => row.line_count_header ?? null, { headerKey: "order.lines" }),
      c.custom(
        "ship_to",
        (row) => row.ship_name ?? "",
        (row) => (
          <span className="flex flex-col leading-tight">
            <span className="truncate text-ink">{row.ship_name ?? ""}</span>
            <span className="truncate text-[11px] text-ink-muted">{row.ship_city ?? ""}</span>
          </span>
        ),
        { headerKey: "column.buyer", defaultVisible: false },
      ),
      c.text("expiry_date", (row) => row.expiry_date ?? "", {
        headerKey: "column.expiry-date",
        filter: "daterange",
        defaultVisible: false,
      }),
      c.custom(
        "deposit",
        (row) => row.deposit ?? 0,
        (row) => <span className="tabular-nums">{money(row.deposit, row.currency)}</span>,
        { headerKey: "column.deposit", filter: "range", align: "right", defaultVisible: false },
      ),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.sales-orders", "Orders")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.sales-orders", "Orders")}
        subtitle={t("orders.subtitle", "Booked orders.")}
      />

      {error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <DataTable
          toolbarExtra={
            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor="orders-from" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("period.from", "From")}
                <Input id="orders-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-36" />
              </label>
              <label htmlFor="orders-to" className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                {t("period.to", "To")}
                <Input id="orders-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-36" />
              </label>
            </div>
          }
          data={data ?? []}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={isLoading}
          selectable
          onSelectionChange={setSelected}
          onRowClick={(row) => setDetailId(row.id)}
          actions={[
            {
              label: t("orders.export.deringer", "Export Deringer"),
              icon: <Download className="size-4" />,
              requiresSelection: true,
              disabled: busy !== null,
              onClick: () =>
                void startExport(
                  "deringer",
                  () => client.documentDeringer(selected.map((row) => row.id)),
                  t("job.download.failed", "Download failed"),
                ),
            },
            {
              label: t("orders.export.deringer-period", "Export Deringer (period)"),
              icon: <Download className="size-4" />,
              disabled: busy !== null,
              onClick: () => setPeriodExportOpen(true),
            },
            {
              label: t("orders.import.tracking", "Import parcel tracking"),
              icon: <Upload className="size-4" />,
              disabled: importing,
              onClick: () => parcelReportInput.current?.click(),
            },
          ]}
          emptyMessage={t("table.empty", "Nothing to show")}
        />
      )}

      {detailId !== null && <OrderDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
      {periodExportOpen && <DeringerPeriodDialog onClose={() => setPeriodExportOpen(false)} />}

      <input
        ref={parcelReportInput}
        type="file"
        // Their current report is a CSV; the .xlsx is the previous format and
        // still parses, so archived reports can be re-run.
        accept=".csv,.xlsx"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice still fires a change event.
          event.target.value = "";
          if (file) void importParcelReport(file);
        }}
      />
    </>
  );
}

function OrderDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new OrderClient(http), [http]);

  const { data, isLoading } = useQuery({
    queryKey: ["order", "detail", id],
    queryFn: () => client.detail(id),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("order.detail.title", "Order {{no}}", { no: data?.no ?? "…" })}
      description={data?.client?.cli_name1 ?? ""}
      size="lg"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      }
    >
      {isLoading || !data ? (
        <div className="flex h-40 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Section title={t("section.general", "General")}>
              <Row label={t("column.order", "Order")} value={data.no} />
              <Row label={t("invoice.type", "Type")} value={invoiceOrderTypeLabel(data.order_type)} />
              <Row label={t("column.status", "Status")} value={data.cancel_date ? "CANCELLED" : data.status} />
              <Row label={t("column.currency", "Currency")} value={data.currency} />
              <Row label={t("invoice.warehouse", "Warehouse")} value={data.warehouse} />
              <Row
                label={t("column.client", "Client")}
                value={
                  data.client
                    ? `${data.client.cli_name1 ?? ""} (${data.client.cli_no ?? ""})`
                    : null
                }
              />
              <Row label={t("invoice.client-order-no", "Client order #")} value={data.client_order_no} />
              <Row label={t("invoice.carrier", "Carrier")} value={data.carrier} />
              <Row label={t("column.carrier-scac", "Carrier SCAC")} value={data.carrier_scac} />
              <Row label={t("column.deposit", "Deposit")} value={money(data.deposit, data.currency)} />
            </Section>

            <Section title={t("section.dates", "Dates")}>
              <Row label={t("invoice.date", "Date")} value={data.date} />
              <Row label={t("column.timestamp", "Timestamp")} value={data.timestamp} />
              <Row label={t("column.expiry-date", "Expiry date")} value={data.expiry_date} />
              <Row label={t("field.cancel-date", "Cancel date")} value={data.cancel_date} />
            </Section>

            <Section title={t("section.ship-to", "Ship to")}>
              <Row label={t("field.name", "Name")} value={data.ship_name} />
              <Row label={t("field.address", "Address")} value={data.ship_address} />
              {data.ship_name2 && <Row label={t("field.address-2", "Address line 2")} value={data.ship_name2} />}
              <Row label={t("field.city", "City")} value={data.ship_city} />
              <Row label={t("field.postal-code", "Postal code")} value={data.ship_postal_code} />
              <Row label={t("field.phone", "Phone")} value={data.ship_phone} />
              <Row label={t("field.email", "Email")} value={data.ship_email} />
            </Section>

            <Section title={t("section.notes", "Notes")}>
              {[data.note1, data.note2, data.note3, data.note4].every((note) => !note) ? (
                <p className="text-[13px] text-ink-faint"></p>
              ) : (
                [data.note1, data.note2, data.note3, data.note4]
                  .filter(Boolean)
                  .map((note, index) => (
                    <p key={index} className="text-[13px] text-ink-muted">
                      {note}
                    </p>
                  ))
              )}
            </Section>
          </div>

          <Card className="overflow-x-auto p-3">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-wider text-ink-faint">
                  <th className="py-1.5 pr-3 text-left">{t("column.line", "Line")}</th>
                  <th className="py-1.5 pr-3 text-left">{t("column.sku", "SKU")}</th>
                  <th className="py-1.5 pr-3 text-left">{t("column.name", "Description")}</th>
                  <th className="py-1.5 pr-3 text-left">{t("order.line.warehouse", "Dispatch")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("order.line.ordered", "Ordered")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("order.line.shipped", "Shipped")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("order.line.cancelled", "Cancelled")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("order.line.open", "Open")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("column.reserved-qty", "Reserved")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("invoice.line.price", "Price")}</th>
                  <th className="py-1.5 pr-3 text-right">{t("column.line-total", "Line total")}</th>
                  <th className="py-1.5 text-left">{t("column.requested-date", "Requested")}</th>
                </tr>
              </thead>
              <tbody>
                {(data.lines ?? []).map((line) => (
                  <tr key={line.id} className="border-b border-line-soft last:border-0">
                    <td className="tabular py-1.5 pr-3">{line.line_no ?? ""}</td>
                    <td className="py-1.5 pr-3 font-medium text-ink">{line.part?.no ?? ""}</td>
                    <td className="py-1.5 pr-3 text-ink-muted">{line.part?.description1 ?? ""}</td>
                    <td className="py-1.5 pr-3">{line.warehouse ?? ""}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{line.ordered_qty ?? 0}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{line.ship_qty ?? 0}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{line.cancel_qty ?? 0}</td>
                    <td className="tabular py-1.5 pr-3 text-right font-semibold">{openQty(line)}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{line.reserved_qty ?? 0}</td>
                    <td className="tabular py-1.5 pr-3 text-right">{money(line.price, data.currency)}</td>
                    <td className="tabular py-1.5 pr-3 text-right">
                      {money(orderLineTotal(line), data.currency)}
                    </td>
                    <td className="py-1.5 text-ink-muted">{line.requested_date ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </Dialog>
  );
}

/**
 * The partner-period export. Clients are limited to the five USD retail
 * partners the feed covers, all preselected — the common case is "everything,
 * this window".
 */
function DeringerPeriodDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const { start: startExport, busy } = useExport();
  const orders = useMemo(() => new OrderClient(http), [http]);
  const clients = useMemo(() => new SigmClientClient(http), [http]);

  const [clientIds, setClientIds] = useState<string[]>(DERINGER_CLIENT_IDS.map(String));
  const [preset, setPreset] = useState<DeringerPeriod>("TODAY");
  const [removeHeader, setRemoveHeader] = useState(true);

  const { data: partnerClients } = useQuery({
    queryKey: ["client", "deringer-partners"],
    queryFn: () => clients.findByIds(DERINGER_CLIENT_IDS),
    staleTime: 30 * 60_000,
  });

  const run = async () => {
    const period = deringerPeriodDates(preset);
    await startExport(
      "deringer-period",
      () => orders.documentDeringerForPeriod(clientIds.map(Number), period, removeHeader),
      t("job.download.failed", "Download failed"),
    );
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("orders.export.deringer-period", "Export Deringer (period)")}
      description={t("orders.export.deringer.description", "Non-cancelled USD orders, one row per line.")}
      size="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            loading={busy !== null}
            disabled={clientIds.length === 0}
            onClick={() => void run()}
          >
            {t("button.export", "Export")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t("orders.export.clients", "Clients")} htmlFor="deringer-clients">
          <MultiSelect
            id="deringer-clients"
            options={(partnerClients ?? []).map((entry) => ({
              value: String(entry.cli_id),
              label: entry.cli_name1 ?? `#${entry.cli_id}`,
            }))}
            value={clientIds}
            onChange={setClientIds}
          />
        </Field>
        <Field label={t("orders.export.period", "Period")} htmlFor="deringer-period">
          <Select
            id="deringer-period"
            value={preset}
            options={DERINGER_PERIODS.map((value) => ({ value, label: value.replaceAll("_", " ") }))}
            onChange={(event) => setPreset(event.target.value as DeringerPeriod)}
          />
        </Field>
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            checked={removeHeader}
            onChange={(event) => setRemoveHeader(event.target.checked)}
            className="size-3.5 accent-[var(--accent)]"
          />
          {t("orders.export.remove-header", "Remove header row")}
        </label>
      </div>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="p-3">
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
        {title}
      </h4>
      {children}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <p className="flex items-baseline justify-between gap-3 py-0.5 text-[13px]">
      <span className="text-ink-muted">{label}</span>
      <span className="tabular text-ink">{value === null || value === undefined || value === "" ? "" : value}</span>
    </p>
  );
}

function money(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency ?? "CAD",
    }).format(value);
  } catch {
    return value.toFixed(2);
  }
}
